import React, { useEffect, useRef, useState } from 'react';
import './UserDetailPopup.css';
import SignatureForm from './SignatureForm';
import { ReactComponent as SignatureIcon } from '../icons/signatures.svg';
import { ReactComponent as SuccessIcon } from '../icons/success-circle.svg';
import { ReactComponent as ErrorIcon } from '../icons/error-circle.svg';
import { api, endpoints } from '../config/api';
import { PERSONAL_PLACEHOLDERS } from '../constants/signaturePlaceholders';
import { describeDeliveryOutcome, deliveryStatus } from '../constants/deliveryOutcomes';
import { useAuth } from '../contexts/AuthContext';
import useOrganizationBrandColors from '../hooks/useOrganizationBrandColors';
import useOrganizationBrandAssets from '../hooks/useOrganizationBrandAssets';
import useOverlayClose from '../hooks/useOverlayClose';
import { getInitials } from '../utils/initials';
import { useT } from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const INFO_FIELDS = ['GivenName', 'Surname', 'JobTitle', 'officeLocation', 'StreetAddress', 'TelephoneNumber', 'Mail', 'Mobile'];

const messages = {
  GivenName: { tr: 'Ad', en: 'First name' },
  Surname: { tr: 'Soyad', en: 'Last name' },
  JobTitle: { tr: 'Unvan', en: 'Job title' },
  officeLocation: { tr: 'Ofis', en: 'Office' },
  StreetAddress: { tr: 'Adres', en: 'Address' },
  TelephoneNumber: { tr: 'Telefon', en: 'Phone' },
  Mail: { tr: 'E-posta', en: 'Email' },
  Mobile: { tr: 'Cep Telefonu', en: 'Mobile' },
  templateNotFound: { tr: 'Şablon bulunamadı.', en: 'Template not found.' },
  saveOverrideFailed: { tr: 'Kullanıcıya özel imza kaydedilemedi: {msg}', en: 'Couldn’t save the personal signature: {msg}' },
  confirmRevert: { tr: 'Bu kullanıcının kişiye özel imzası kaldırılıp grup bazlı imzaya döndürülsün mü?', en: 'Remove this user’s personal signature and return to the group-based signature?' },
  revertFailed: { tr: 'Grup bazlı imzaya döndürülemedi: {msg}', en: 'Couldn’t return to the group-based signature: {msg}' },
  previewFailed: { tr: 'Önizleme yüklenemedi.', en: 'Couldn’t load the preview.' },
  primaryFailed: { tr: 'Birincil şablon güncellenemedi: {msg}', en: 'Couldn’t update the primary template: {msg}' },
  selectableFailed: { tr: 'Seçilebilir şablonlar güncellenemedi: {msg}', en: 'Couldn’t update selectable templates: {msg}' },
  groups: { tr: 'Gruplar', en: 'Groups' },
  noGroups: { tr: 'Herhangi bir gruba dahil değil', en: 'Not in any group' },
  primaryTemplate: { tr: 'Birincil Şablon', en: 'Primary Template' },
  primaryAuto: { tr: 'Otomatik (grup kurallarına göre)', en: 'Automatic (by group rules)' },
  primaryHint: { tr: 'Kişiye özel imza aktifken bu ayarın görünür bir etkisi olmaz.', en: 'This setting has no visible effect while a personal signature is active.' },
  selectable: { tr: 'Seçilebilir Şablonlar', en: 'Selectable Templates' },
  selectedCount: { tr: '{n} şablon seçili', en: ({ n }) => `${n} template${n === 1 ? '' : 's'} selected` },
  noneSelected: { tr: 'Şablon seçilmedi', en: 'No templates selected' },
  selectableHint: {
    tr: 'Bu kullanıcı, burada işaretlenen şablonlar arasından Outlook eklentisinde kendi imza şablonunu seçebilir.',
    en: 'This user can choose their own signature template in the Outlook add-in from the templates checked here.',
  },
  info: { tr: 'Bilgiler', en: 'Details' },
  deliveryHistory: { tr: 'Teslimat Geçmişi', en: 'Delivery History' },
  signaturePreview: { tr: 'İmza Önizlemesi', en: 'Signature Preview' },
  personal: { tr: 'Kişiye özel', en: 'Personal' },
  userChoice: { tr: 'Kullanıcının seçimi', en: 'User’s choice' },
  timeout: { tr: 'Sunucudan yanıt alınamadı. Lütfen tekrar deneyin.', en: 'No response from the server. Please try again.' },
  customizeTitle: { tr: '{email} İçin İmzayı Özelleştir', en: 'Customize Signature for {email}' },
  previewNote: {
    tr: 'Bu, kullanıcının seçebileceği "{name}" şablonunun kendi verileriyle önizlemesi — aktif imzası değil.',
    en: 'This is a preview of the selectable "{name}" template with the user’s own data — not their active signature.',
  },
  customize: { tr: 'Özelleştir', en: 'Customize' },
  reverting: { tr: 'Döndürülüyor...', en: 'Reverting...' },
  revert: { tr: 'Grup Bazlıya Döndür', en: 'Revert to Group Signature' },
};

function UserDetailPopup({ user, onClose }) {
  const overlayClose = useOverlayClose(onClose);
  const { t, formatDateTime } = useT(messages);
  const { user: currentUser } = useAuth();
  const [brandColors] = useOrganizationBrandColors();
  const [brandAssets] = useOrganizationBrandAssets();
  const email = user?.Mail || user?.mail || '';
  const groups = user?.groups && user.groups.length > 0 ? user.groups : [user?.group].filter(Boolean);

  const [templateHTML, setTemplateHTML] = useState('');
  const [templateSource, setTemplateSource] = useState(null);
  // Şu an gerçekten aktif olan şablonun id'si (override'da id kavramı yok,
  // o zaman null kalır) — hangi swatch'ın "bu zaten gösterilen" olduğunu
  // ayrı bir "Güncel" pill'i olmadan işaretlemek için kullanılıyor.
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeoutError, setTimeoutError] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editHtml, setEditHtml] = useState('');
  const [photoLoaded, setPhotoLoaded] = useState(false);
  const [reverting, setReverting] = useState(false);
  // Birincil şablon = kullanıcıya doğrudan atanan şablon (şablon
  // düzenleyicideki "Kişiye Özel Atama" ile aynı kayıt); '' = grup kuralları.
  const [primaryTemplateId, setPrimaryTemplateId] = useState('');
  const [primaryTemplateSaving, setPrimaryTemplateSaving] = useState(false);
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [selectableIds, setSelectableIds] = useState([]);
  const [selectableSaving, setSelectableSaving] = useState(false);
  const [showSelectableDropdown, setShowSelectableDropdown] = useState(false);
  const selectableDropdownRef = useRef(null);
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  // İmza Önizlemesi swatch'ları: null = şu an gerçekten aktif olan imza
  // (templateHTML, override/tercih/grup zincirinin sonucu). Bir şablon
  // id'si = o SPESİFİK seçilebilir şablonun bu kullanıcı için nasıl
  // görüneceğinin önizlemesi (gerçek verilerle, /user-template?template_id=
  // ile), zincir tamamen bypass edilerek.
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const fetchTemplate = (signal) => {
    return api.get(`/api/signatures/user-template/?email=${email}`, signal ? { signal } : undefined)
      .then(data => {
        setTemplateHTML(data.template_html || '');
        setTemplateSource(data.source || null);
        setCurrentTemplateId(data.template?.id ?? null);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        console.error('Şablon alınamadı:', err);
        setTemplateHTML(`<p style="color:red;">${t('templateNotFound')}</p>`);
        setTemplateSource(null);
        setCurrentTemplateId(null);
      });
  };

  const fetchPrimaryTemplate = (signal) => {
    return api.get(`/api/signatures/user-primary-template/?email=${encodeURIComponent(email)}`, signal ? { signal } : undefined)
      .then(data => setPrimaryTemplateId(data.template_id ? String(data.template_id) : ''))
      .catch(err => {
        if (err.name === 'AbortError') return;
        setPrimaryTemplateId('');
      });
  };

  const fetchSelectableTemplates = (signal) => {
    const opts = signal ? { signal } : undefined;
    return Promise.all([
      api.get(endpoints.signatures.templates.list, opts),
      api.get(`${endpoints.signatures.userSelectableTemplates}?email=${encodeURIComponent(email)}`, opts),
    ])
      .then(([templates, selectable]) => {
        setOrgTemplates(templates || []);
        setSelectableIds(selectable?.template_ids || []);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setOrgTemplates([]);
        setSelectableIds([]);
      });
  };

  // Son ~5 teslimat denemesi — bkz. SignatureDeliveryLog. 404/hata durumunda
  // sessizce boş liste gösterilir, bu popup'ın asıl işini (imza önizleme)
  // engellemez.
  const fetchDeliveryHistory = (signal) => {
    const opts = { params: { email, page_size: 5 }, ...(signal ? { signal } : {}) };
    return api.get(endpoints.signatures.deliveryLog.list, opts)
      .then(data => setDeliveryHistory(data.results || []))
      .catch(err => {
        if (err.name === 'AbortError') return;
        setDeliveryHistory([]);
      });
  };

  // Dropdown dışına tıklanınca kapat — modal'in kendisi zaten backdrop'a
  // tıklayınca kapanıyor (useOverlayClose), bu ayrıca modal İÇİNDEKİ küçük
  // açılır panel için aynı fikri uyguluyor.
  useEffect(() => {
    if (!showSelectableDropdown) return;
    const handleClickOutside = (e) => {
      if (selectableDropdownRef.current && !selectableDropdownRef.current.contains(e.target)) {
        setShowSelectableDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSelectableDropdown]);

  useEffect(() => {
    if (!email) return;
    setTimeoutError(false);
    setEditMode(false);
    setEditHtml('');
    setPhotoLoaded(false);
    setShowSelectableDropdown(false);
    setPreviewTemplateId(null);
    setPreviewHtml('');
    setLoading(true);
    const controller = new AbortController();
    const timeout = setTimeout(() => {
      controller.abort();
      setTimeoutError(true);
      setLoading(false);
    }, 10000); // 10 saniye

    fetchTemplate(controller.signal)
      .finally(() => {
        clearTimeout(timeout);
        setLoading(false);
      });
    fetchPrimaryTemplate(controller.signal);
    fetchSelectableTemplates(controller.signal);
    fetchDeliveryHistory(controller.signal);
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [email]);

  const handleEdit = () => {
    setEditHtml(typeof templateHTML === 'string' ? templateHTML : '');
    setPreviewTemplateId(null); // her zaman gerçek aktif imzadan düzenle, bir swatch önizlemesinden değil
    setEditMode(true);
  };

  const handleSave = async (payload) => {
    try {
      await api.post('/api/signatures/user-signature-override/', {
        email,
        html_content: payload.html
      });
      setEditMode(false);
      setEditHtml('');
      setPreviewTemplateId(null);
      // Kaydettikten sonra güncel imzayı tekrar çek
      setLoading(true);
      await fetchTemplate();
    } catch (err) {
      alert(t('saveOverrideFailed', { msg: err.message }));
    } finally {
      setLoading(false);
    }
  };

  const handleRevertToGroup = async () => {
    if (!window.confirm(t('confirmRevert'))) {
      return;
    }
    setReverting(true);
    try {
      await api.delete(`/api/signatures/user-signature-override/?email=${encodeURIComponent(email)}`);
      setPreviewTemplateId(null);
      setLoading(true);
      await fetchTemplate();
    } catch (err) {
      alert(t('revertFailed', { msg: err.message }));
    } finally {
      setReverting(false);
      setLoading(false);
    }
  };

  // Bir şablon id'si için o şablonu bu kullanıcının gerçek verileriyle
  // sunucudan render ettirip önizler.
  const handlePreviewTemplate = async (templateId) => {
    setPreviewTemplateId(templateId);
    setPreviewLoading(true);
    try {
      const data = await api.get(
        '/api/signatures/user-template/',
        { params: { email, template_id: templateId } }
      );
      setPreviewHtml(data.template_html || '');
    } catch (err) {
      setPreviewHtml(`<p style="color:red;">${t('previewFailed')}</p>`);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Ayrı bir "Güncel" pill'i yok — hangi swatch'ın şu an ekranda olduğu,
  // previewTemplateId set edilmemişse currentTemplateId'ye göre otomatik
  // işaretleniyor (bkz. aşağıdaki render). Zaten aktif olan bir swatch'a
  // (gerçek güncel olsun ya da önizlemede olsun) tekrar tıklamak gerçek
  // güncel imzaya döner — ayrı bir "geri dön" düğmesine gerek kalmadan.
  const handleSwatchClick = (templateId) => {
    const isAlreadyShown = previewTemplateId !== null
      ? previewTemplateId === templateId
      : currentTemplateId === templateId;
    if (isAlreadyShown || templateId === currentTemplateId) {
      setPreviewTemplateId(null);
    } else {
      handlePreviewTemplate(templateId);
    }
  };

  const handlePrimaryTemplateChange = async (e) => {
    const value = e.target.value;
    setPrimaryTemplateSaving(true);
    try {
      if (value) {
        await api.put('/api/signatures/user-primary-template/', { email, template_id: Number(value) });
      } else {
        await api.delete(`/api/signatures/user-primary-template/?email=${encodeURIComponent(email)}`);
      }
      setPrimaryTemplateId(value);
      setPreviewTemplateId(null);
      await fetchTemplate();
    } catch (err) {
      alert(t('primaryFailed', { msg: err.message }));
    } finally {
      setPrimaryTemplateSaving(false);
    }
  };

  const handleSelectableTemplateToggle = async (templateId, checked) => {
    const nextIds = checked
      ? [...selectableIds, templateId]
      : selectableIds.filter(id => id !== templateId);
    const previousIds = selectableIds;
    setSelectableIds(nextIds); // optimistic
    setSelectableSaving(true);
    try {
      await api.put(endpoints.signatures.userSelectableTemplates, { email, template_ids: nextIds });
    } catch (err) {
      setSelectableIds(previousIds);
      alert(t('selectableFailed', { msg: err.message }));
    } finally {
      setSelectableSaving(false);
    }
  };

  const activeTemplates = orgTemplates.filter(tpl => tpl.is_active !== false);
  const organizationId = currentUser?.organization;
  const photoUrl = organizationId && email
    ? `${API_BASE_URL}/api/signatures/user-photo/?org=${organizationId}&email=${encodeURIComponent(email)}`
    : null;

  return (
    <div className="modal-overlay" {...overlayClose}>
      <div className="popup-box user-detail-popup">
        <button className="close-btn" onClick={onClose}>×</button>

        {!editMode && (
          <>
            <div className="user-detail-header">
              <div className="user-detail-avatar-wrap">
                <div className="user-detail-avatar-initials">{getInitials(user)}</div>
                {photoUrl && (
                  <img
                    src={photoUrl}
                    alt=""
                    className={`user-detail-avatar-photo${photoLoaded ? ' loaded' : ''}`}
                    onLoad={(e) => { if (e.target.naturalWidth > 1) setPhotoLoaded(true); }}
                  />
                )}
              </div>
              <div>
                <h3 className="user-detail-name">{user?.displayName || email}</h3>
                <p className="user-detail-subtitle">{email}{user?.JobTitle ? ` · ${user.JobTitle}` : ''}</p>
              </div>
            </div>

            <div className="user-detail-section">
              <div className="user-detail-section-title">{t('groups')}</div>
              <div className="user-detail-group-pills">
                {groups.length > 0
                  ? groups.map(g => <span className="user-detail-group-pill" key={g}>{g}</span>)
                  : <span className="user-detail-empty">{t('noGroups')}</span>}
              </div>
              {activeTemplates.length > 0 && (
                <div className="user-detail-primary-group">
                  <label htmlFor="primaryTemplateSelect" className="user-detail-primary-group-label">
                    {t('primaryTemplate')}
                  </label>
                  <select
                    id="primaryTemplateSelect"
                    className="user-detail-primary-group-select"
                    value={primaryTemplateId}
                    onChange={handlePrimaryTemplateChange}
                    disabled={primaryTemplateSaving}
                  >
                    <option value="">{t('primaryAuto')}</option>
                    {activeTemplates.map(tpl => <option value={String(tpl.id)} key={tpl.id}>{tpl.name}</option>)}
                  </select>
                  {templateSource === 'override' && (
                    <p className="user-detail-primary-group-hint">
                      {t('primaryHint')}
                    </p>
                  )}
                </div>
              )}
              {orgTemplates.length >= 2 && (
                <div className="user-detail-primary-group" ref={selectableDropdownRef}>
                  <label className="user-detail-primary-group-label">
                    {t('selectable')}
                  </label>
                  <button
                    type="button"
                    className="user-detail-primary-group-select user-detail-selectable-trigger"
                    onClick={() => setShowSelectableDropdown(o => !o)}
                    disabled={selectableSaving}
                  >
                    {selectableIds.length > 0 ? t('selectedCount', { n: selectableIds.length }) : t('noneSelected')}
                    <span className="user-detail-selectable-caret">{showSelectableDropdown ? '▴' : '▾'}</span>
                  </button>
                  {showSelectableDropdown && (
                    <div className="user-detail-selectable-panel">
                      <p className="user-detail-primary-group-hint">
                        {t('selectableHint')}
                      </p>
                      <div className="user-detail-selectable-templates">
                        {orgTemplates.map(tpl => (
                          <label className="user-detail-selectable-template" key={tpl.id}>
                            <input
                              type="checkbox"
                              checked={selectableIds.includes(tpl.id)}
                              disabled={selectableSaving}
                              onChange={e => handleSelectableTemplateToggle(tpl.id, e.target.checked)}
                            />
                            {tpl.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="user-detail-section">
              <div className="user-detail-section-title">{t('info')}</div>
              <div className="user-detail-info-grid">
                {INFO_FIELDS.map(key => (
                  <div className="user-detail-info-item" key={key}>
                    <div className="user-detail-info-label">{t(key)}</div>
                    <div className="user-detail-info-value">{user?.[key] || '—'}</div>
                  </div>
                ))}
              </div>
            </div>

            {deliveryHistory.length > 0 && (
              <div className="user-detail-section">
                <div className="user-detail-section-title">{t('deliveryHistory')}</div>
                <ul className="user-detail-delivery-history">
                  {deliveryHistory.map(entry => {
                    const { label, reason } = describeDeliveryOutcome(entry);
                    return (
                      <li key={entry.id} className={`is-${deliveryStatus(entry)}`}>
                        <span className="user-detail-delivery-date">
                          {formatDateTime(entry.created_at)}
                        </span>
                        <span className="user-detail-delivery-outcome">
                          {entry.is_success ? <SuccessIcon className="user-detail-delivery-icon" /> : <ErrorIcon className="user-detail-delivery-icon" />}
                          {entry.is_success ? (entry.template_name || entry.matched_group || label) : label}
                        </span>
                        {reason && <span className="user-detail-delivery-reason">{reason}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="user-detail-section-title user-detail-signature-title">
              {t('signaturePreview')}
              {templateSource === 'override' && (
                <span className="user-detail-source-badge">{t('personal')}</span>
              )}
              {templateSource === 'user_selected' && (
                <span className="user-detail-source-badge">{t('userChoice')}</span>
              )}
            </div>
          </>
        )}

        {loading ? (
          <p>{t('loading')}</p>
        ) : timeoutError ? (
          <p style={{ color: 'red' }}>{t('timeout')}</p>
        ) : editMode ? (
          <SignatureForm
            onSave={handleSave}
            onCancel={() => setEditMode(false)}
            title={t('customizeTitle', { email })}
            initialHtml={typeof editHtml === 'string' ? editHtml : ''}
            initialName={email}
            groups={[]}
            parameters={PERSONAL_PLACEHOLDERS}
            allowSurvey={false}
            brandColors={brandColors}
            brandAssets={brandAssets}
          />
        ) : (
          <>
            {selectableIds.length > 0 && (
              <div className="user-detail-preview-swatches">
                {orgTemplates
                  .filter(tpl => selectableIds.includes(tpl.id))
                  .map(tpl => {
                    const isActive = previewTemplateId !== null
                      ? previewTemplateId === tpl.id
                      : currentTemplateId === tpl.id;
                    return (
                      <button
                        type="button"
                        key={tpl.id}
                        className={`user-detail-swatch${isActive ? ' active' : ''}`}
                        onClick={() => handleSwatchClick(tpl.id)}
                      >
                        {tpl.name}
                      </button>
                    );
                  })}
              </div>
            )}

            {previewTemplateId !== null && (
              <p className="user-detail-empty">
                {t('previewNote', { name: orgTemplates.find(tpl => tpl.id === previewTemplateId)?.name })}
              </p>
            )}

            <div
              className="signature-preview"
              dangerouslySetInnerHTML={{
                __html: previewTemplateId === null
                  ? templateHTML
                  : (previewLoading ? `<p>${t('loading')}</p>` : previewHtml)
              }}
            />

            {previewTemplateId === null && (
              <div className="user-detail-signature-actions">
                <button className="edit-btn" onClick={handleEdit}>
                  <SignatureIcon/> {t('customize')}
                </button>
                {templateSource === 'override' && (
                  <button
                    className="revert-to-group-btn"
                    onClick={handleRevertToGroup}
                    disabled={reverting}
                  >
                    {reverting ? t('reverting') : t('revert')}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default UserDetailPopup;
